import { FiUser, FiCheck, FiClock } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { PanelMemberRole } from "../../../../../types/panel.type";
import { getRoleBadgeClasses, formatMemberDate } from "./memberPresentation";
import type { FinalPanelMemberData } from "./types";

interface FinalMemberRowProps {
	member: FinalPanelMemberData;
}

export const FinalMemberRow: React.FC<FinalMemberRowProps> = ({ member }) => {
	const progressPercent = member.totalSuppliers > 0
		? Math.round((member.suppliersCompleted / member.totalSuppliers) * 100)
		: 0;

	return (
		<tr
			className="border-b border-gray-100 transition-colors hover:bg-gray-50"
			data-testid={`panel-member-row-${member.memberId}`}
		>
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500">
						<FiUser size={14} aria-hidden="true" />
					</div>
					<div>
						<p className="font-medium text-gray-900 text-sm">
							{member.memberName || member.email}
						</p>
						{member.memberName && (
							<p className="text-xs text-gray-500">{member.email}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
						getRoleBadgeClasses(member.role as PanelMemberRole)
					)}
				>
					{member.role === "rfxLead" ? "RFx Lead" : member.role}
				</span>
			</td>
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="h-2 w-20 rounded-full bg-gray-200" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100}>
						<div
							className={cn(
								"h-full rounded-full transition-all",
								progressPercent === 100
									? "bg-green-500"
									: progressPercent > 0
										? "bg-[#0031DD]"
										: "bg-gray-200"
							)}
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
					<span className="text-xs text-gray-500 tabular-nums">
						{member.suppliersCompleted}/{member.totalSuppliers}
					</span>
				</div>
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
						member.isSubmitted
							? "bg-green-100 text-green-700"
							: "bg-amber-100 text-amber-700"
					)}
				>
					{member.isSubmitted ? (
						<><FiCheck size={12} aria-hidden="true" /> Final scores submitted</>
					) : (
						<><FiClock size={12} aria-hidden="true" /> Awaiting final scores</>
					)}
				</span>
			</td>
			<td className="px-3 py-3 text-xs text-gray-500">
				{formatMemberDate(member.submittedAt)}
			</td>
		</tr>
	);
};
